/**
 * User Search Picker Component
 *
 * A searchable user selection component for reassignment and delegation.
 * Features:
 * - Search-as-you-type with debouncing
 * - Displays user results with name and email
 * - Single user selection
 * - Shows selected user with clear option
 * - Loading and empty states
 * - Styled dropdown with glass morphism
 * - Keyboard navigation support
 */

import { createInput } from './input';
import { renderIcon, createSpinner } from '../utils/icons';
import { apiClient } from '../services/api-client';
import type { ValidationState } from '../types/form';

/**
 * Directory user type matching backend response
 */
export interface DirectoryUser {
  id: string;
  displayName: string;
  email: string | null;
  userPrincipalName: string;
  givenName: string | null;
  surname: string | null;
  jobTitle: string | null;
  department: string | null;
  officeLocation: string | null;
  mobilePhone: string | null;
  businessPhones: string[];
  accountEnabled: boolean;
  photoUrl: string | null;
}

/**
 * Configuration options for the User Search Picker
 */
export interface UserSearchPickerOptions {
  /** Unique identifier for the component */
  id?: string;
  /** Name attribute for form submission */
  name?: string;
  /** Placeholder text for the search input */
  placeholder?: string;
  /** Whether the field is disabled */
  disabled?: boolean;
  /** Whether the field is required */
  required?: boolean;
  /** Current validation state */
  validationState?: ValidationState;
  /** CSS class name(s) */
  className?: string;
  /** Minimum characters to trigger search */
  minSearchLength?: number;
  /** Debounce delay in milliseconds */
  debounceMs?: number;
  /** Maximum results to show */
  maxResults?: number;
  /** Initially selected user */
  selectedUser?: DirectoryUser | null;
  /** Callback when user is selected */
  onSelect?: (user: DirectoryUser | null) => void;
  /** Callback when search query changes */
  onSearch?: (query: string) => void;
  /** Filter by department */
  department?: string;
}

/**
 * API response for user search
 */
interface UserSearchResponse {
  data: DirectoryUser[];
  message: string;
}

/**
 * Default options
 */
const DEFAULT_OPTIONS: Required<Omit<UserSearchPickerOptions, 'id' | 'name' | 'selectedUser' | 'onSelect' | 'onSearch' | 'department'>> = {
  placeholder: 'Search for a user...',
  disabled: false,
  required: false,
  validationState: 'default',
  className: '',
  minSearchLength: 2,
  debounceMs: 300,
  maxResults: 10,
};

/**
 * CSS classes for validation states
 */
const STATE_CLASSES: Record<ValidationState, string> = {
  default: 'border-neutral-800',
  error: 'border-destructive',
  success: 'border-success',
  warning: 'border-warning',
};

/**
 * Generate a unique ID if not provided
 */
function generateId(): string {
  return `user-search-picker-${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Create a User Search Picker component
 */
export function createUserSearchPicker(options: UserSearchPickerOptions = {}): HTMLDivElement {
  const config = {
    ...DEFAULT_OPTIONS,
    ...options,
    id: options.id || generateId(),
  };

  // State
  let selectedUser: DirectoryUser | null = options.selectedUser ?? null;
  let searchResults: DirectoryUser[] = [];
  let isLoading = false;
  let isOpen = false;
  let searchQuery = '';
  let highlightedIndex = -1;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let abortController: AbortController | null = null;

  // Create container
  const container = document.createElement('div');
  container.id = config.id;
  container.className = `user-search-picker relative ${config.className}`.trim();

  // Create wrapper for input and selected user display
  const inputWrapper = document.createElement('div');
  inputWrapper.className = 'relative';

  // Create search input - build options object conditionally
  const inputOptions: Parameters<typeof createInput>[0] = {
    id: `${config.id}-input`,
    type: 'search',
    placeholder: config.placeholder,
    disabled: config.disabled,
    required: config.required && !selectedUser,
    validationState: config.validationState,
    autocomplete: 'off',
    attributes: {
      role: 'combobox',
      'aria-expanded': 'false',
      'aria-haspopup': 'listbox',
      'aria-autocomplete': 'list',
      'aria-controls': `${config.id}-results`,
    },
  };
  if (config.name) {
    inputOptions.name = config.name;
  }
  const searchInput = createInput(inputOptions);

  // Add search icon
  const searchIcon = document.createElement('span');
  searchIcon.className = 'absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none';
  searchIcon.appendChild(renderIcon('search', { size: 'sm', decorative: true }));

  // Add loading indicator
  const loadingIndicator = document.createElement('span');
  loadingIndicator.className = 'absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hidden';
  loadingIndicator.appendChild(createSpinner({ size: 'sm' }));

  // Adjust input padding for search icon
  searchInput.classList.add('pl-10');

  // Create dropdown container
  const dropdown = document.createElement('div');
  dropdown.id = `${config.id}-results`;
  dropdown.className = `
    absolute z-50 w-full mt-1 max-h-64 overflow-auto
    rounded-md border bg-neutral-950/90 backdrop-blur-md shadow-lg
    transition-all duration-150
    hidden opacity-0 scale-95
  `.replace(/\s+/g, ' ').trim();
  dropdown.setAttribute('role', 'listbox');
  dropdown.setAttribute('aria-label', 'User search results');

  // Create selected user display
  const selectedDisplay = document.createElement('div');
  selectedDisplay.className = `
    flex items-center gap-3 p-2.5 rounded-md border
    bg-neutral-950/60 backdrop-blur-sm
    hidden
  `.replace(/\s+/g, ' ').trim();

  // Assemble the component
  inputWrapper.appendChild(searchIcon);
  inputWrapper.appendChild(searchInput);
  inputWrapper.appendChild(loadingIndicator);
  container.appendChild(inputWrapper);
  container.appendChild(selectedDisplay);
  container.appendChild(dropdown);

  /**
   * Render the selected user display
   */
  function renderSelectedUser(): void {
    if (!selectedUser) {
      selectedDisplay.classList.add('hidden');
      inputWrapper.classList.remove('hidden');
      searchInput.required = config.required;
      return;
    }

    selectedDisplay.classList.remove('hidden');
    inputWrapper.classList.add('hidden');

    // Update border color based on validation state
    selectedDisplay.className = selectedDisplay.className.replace(/border-\S+/g, '');
    selectedDisplay.classList.add(STATE_CLASSES[config.validationState]);

    selectedDisplay.innerHTML = '';

    // User avatar or initials
    const avatar = document.createElement('div');
    avatar.className = 'w-10 h-10 rounded-full flex items-center justify-center bg-neutral-800 text-foreground shrink-0';

    if (selectedUser.photoUrl) {
      const img = document.createElement('img');
      img.src = selectedUser.photoUrl;
      img.alt = selectedUser.displayName;
      img.className = 'w-full h-full rounded-full object-cover';
      img.onerror = () => {
        img.remove();
        avatar.textContent = getInitials(selectedUser!.displayName);
      };
      avatar.appendChild(img);
    } else {
      avatar.textContent = getInitials(selectedUser.displayName);
    }

    // User info
    const info = document.createElement('div');
    info.className = 'flex-1 min-w-0';

    const nameEl = document.createElement('div');
    nameEl.className = 'text-sm font-medium text-foreground truncate';
    nameEl.textContent = selectedUser.displayName;

    const emailEl = document.createElement('div');
    emailEl.className = 'text-xs text-muted-foreground truncate';
    emailEl.textContent = selectedUser.email || selectedUser.userPrincipalName;

    info.appendChild(nameEl);
    info.appendChild(emailEl);

    // Clear button
    const clearBtn = document.createElement('button');
    clearBtn.type = 'button';
    clearBtn.className = `
      p-1.5 rounded-md text-muted-foreground
      hover:text-foreground hover:bg-neutral-800
      transition-colors focus:outline-none focus:ring-2 focus:ring-ring
    `.replace(/\s+/g, ' ').trim();
    clearBtn.setAttribute('aria-label', 'Clear selected user');
    clearBtn.disabled = config.disabled;
    clearBtn.appendChild(renderIcon('x', { size: 'sm', decorative: true }));
    clearBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      clearSelection();
    });

    selectedDisplay.appendChild(avatar);
    selectedDisplay.appendChild(info);
    selectedDisplay.appendChild(clearBtn);
  }

  /**
   * Clear the current selection
   */
  function clearSelection(): void {
    selectedUser = null;
    renderSelectedUser();
    searchInput.value = '';
    searchInput.focus();
    options.onSelect?.(null);
  }

  /**
   * Render the search results dropdown
   */
  function renderResults(): void {
    dropdown.innerHTML = '';

    if (isLoading) {
      const loadingEl = document.createElement('div');
      loadingEl.className = 'flex items-center justify-center gap-2 p-4 text-muted-foreground';
      loadingEl.appendChild(createSpinner({ size: 'sm' }));
      const text = document.createElement('span');
      text.textContent = 'Searching...';
      loadingEl.appendChild(text);
      dropdown.appendChild(loadingEl);
      return;
    }

    if (searchQuery.length < config.minSearchLength) {
      const hintEl = document.createElement('div');
      hintEl.className = 'p-4 text-sm text-muted-foreground text-center';
      hintEl.textContent = `Type at least ${config.minSearchLength} characters to search`;
      dropdown.appendChild(hintEl);
      return;
    }

    if (searchResults.length === 0) {
      const emptyEl = document.createElement('div');
      emptyEl.className = 'p-4 text-sm text-muted-foreground text-center';
      emptyEl.textContent = 'No users found';
      dropdown.appendChild(emptyEl);
      return;
    }

    searchResults.forEach((user, index) => {
      const option = createUserOption(user, index);
      dropdown.appendChild(option);
    });
  }

  /**
   * Create a user option element
   */
  function createUserOption(user: DirectoryUser, index: number): HTMLDivElement {
    const option = document.createElement('div');
    option.className = `
      flex items-center gap-3 px-3 py-2 cursor-pointer
      transition-colors duration-100
      ${index === highlightedIndex ? 'bg-neutral-800' : 'hover:bg-neutral-800/50'}
    `.replace(/\s+/g, ' ').trim();
    option.setAttribute('role', 'option');
    option.setAttribute('aria-selected', String(index === highlightedIndex));
    option.setAttribute('data-index', String(index));
    option.id = `${config.id}-option-${index}`;

    // User avatar or initials
    const avatar = document.createElement('div');
    avatar.className = 'w-8 h-8 rounded-full flex items-center justify-center bg-neutral-800 text-foreground text-xs shrink-0';

    if (user.photoUrl) {
      const img = document.createElement('img');
      img.src = user.photoUrl;
      img.alt = user.displayName;
      img.className = 'w-full h-full rounded-full object-cover';
      img.onerror = () => {
        img.remove();
        avatar.textContent = getInitials(user.displayName);
      };
      avatar.appendChild(img);
    } else {
      avatar.textContent = getInitials(user.displayName);
    }

    // User info
    const info = document.createElement('div');
    info.className = 'flex-1 min-w-0';

    const nameRow = document.createElement('div');
    nameRow.className = 'flex items-center gap-2';

    const nameEl = document.createElement('span');
    nameEl.className = 'text-sm font-medium text-foreground truncate';
    nameEl.textContent = user.displayName;

    nameRow.appendChild(nameEl);

    if (user.jobTitle) {
      const titleEl = document.createElement('span');
      titleEl.className = 'text-xs text-muted-foreground truncate';
      titleEl.textContent = user.jobTitle;
      nameRow.appendChild(titleEl);
    }

    const emailEl = document.createElement('div');
    emailEl.className = 'text-xs text-muted-foreground truncate';
    emailEl.textContent = user.email || user.userPrincipalName;

    info.appendChild(nameRow);
    info.appendChild(emailEl);

    option.appendChild(avatar);
    option.appendChild(info);

    // Click handler
    option.addEventListener('click', () => {
      selectUser(user);
    });

    // Mouse enter handler for highlighting
    option.addEventListener('mouseenter', () => {
      highlightedIndex = index;
      updateHighlight();
    });

    return option;
  }

  /**
   * Update the highlight state
   */
  function updateHighlight(): void {
    const options = dropdown.querySelectorAll('[role="option"]');
    options.forEach((opt, index) => {
      const isHighlighted = index === highlightedIndex;
      opt.classList.toggle('bg-neutral-800', isHighlighted);
      opt.classList.toggle('hover:bg-neutral-800/50', !isHighlighted);
      opt.setAttribute('aria-selected', String(isHighlighted));
    });

    // Update aria-activedescendant
    if (highlightedIndex >= 0) {
      searchInput.setAttribute('aria-activedescendant', `${config.id}-option-${highlightedIndex}`);
    } else {
      searchInput.removeAttribute('aria-activedescendant');
    }
  }

  /**
   * Select a user
   */
  function selectUser(user: DirectoryUser): void {
    selectedUser = user;
    closeDropdown();
    renderSelectedUser();
    options.onSelect?.(user);
  }

  /**
   * Open the dropdown
   */
  function openDropdown(): void {
    if (isOpen || config.disabled) return;

    isOpen = true;
    dropdown.classList.remove('hidden');

    // Trigger animation
    requestAnimationFrame(() => {
      dropdown.classList.remove('opacity-0', 'scale-95');
      dropdown.classList.add('opacity-100', 'scale-100');
    });

    searchInput.setAttribute('aria-expanded', 'true');
  }

  /**
   * Close the dropdown
   */
  function closeDropdown(): void {
    if (!isOpen) return;

    isOpen = false;
    dropdown.classList.remove('opacity-100', 'scale-100');
    dropdown.classList.add('opacity-0', 'scale-95');

    // Hide after animation
    setTimeout(() => {
      if (!isOpen) {
        dropdown.classList.add('hidden');
      }
    }, 150);

    searchInput.setAttribute('aria-expanded', 'false');
    highlightedIndex = -1;
  }

  /**
   * Set loading state
   */
  function setLoading(loading: boolean): void {
    isLoading = loading;
    if (loading) {
      loadingIndicator.classList.remove('hidden');
    } else {
      loadingIndicator.classList.add('hidden');
    }
    renderResults();
  }

  /**
   * Search users
   */
  async function searchUsers(query: string): Promise<void> {
    // Cancel previous request
    if (abortController) {
      abortController.abort();
    }

    if (query.length < config.minSearchLength) {
      searchResults = [];
      renderResults();
      return;
    }

    abortController = new AbortController();
    setLoading(true);

    try {
      const params: Record<string, string | number> = {
        q: query,
        limit: config.maxResults,
      };

      if (options.department) {
        params.department = options.department;
      }

      const response = await apiClient.get<UserSearchResponse>('/users/search', {
        params,
        signal: abortController.signal,
      });

      searchResults = response.data.data;
      highlightedIndex = searchResults.length > 0 ? 0 : -1;
      setLoading(false);
    } catch (error) {
      // Ignore abort errors
      if (error instanceof Error && error.name === 'AbortError') {
        return;
      }

      console.error('Error searching users:', error);
      searchResults = [];
      setLoading(false);
    }
  }

  /**
   * Handle input changes with debouncing
   */
  function handleInput(): void {
    const query = searchInput.value.trim();
    searchQuery = query;

    options.onSearch?.(query);

    // Clear existing timer
    if (debounceTimer) {
      clearTimeout(debounceTimer);
    }

    // Open dropdown
    if (!isOpen) {
      openDropdown();
    }

    // Debounce search
    debounceTimer = setTimeout(() => {
      searchUsers(query);
    }, config.debounceMs);
  }

  /**
   * Handle keyboard navigation
   */
  function handleKeyDown(event: KeyboardEvent): void {
    if (!isOpen) {
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        openDropdown();
        event.preventDefault();
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        if (highlightedIndex < searchResults.length - 1) {
          highlightedIndex++;
          updateHighlight();
          scrollToHighlighted();
        }
        break;

      case 'ArrowUp':
        event.preventDefault();
        if (highlightedIndex > 0) {
          highlightedIndex--;
          updateHighlight();
          scrollToHighlighted();
        }
        break;

      case 'Enter':
        event.preventDefault();
        const selectedResult = searchResults[highlightedIndex];
        if (highlightedIndex >= 0 && selectedResult) {
          selectUser(selectedResult);
        }
        break;

      case 'Escape':
        event.preventDefault();
        closeDropdown();
        searchInput.blur();
        break;

      case 'Tab':
        closeDropdown();
        break;
    }
  }

  /**
   * Scroll dropdown to keep highlighted option visible
   */
  function scrollToHighlighted(): void {
    const highlighted = dropdown.querySelector(`#${config.id}-option-${highlightedIndex}`);
    if (highlighted && typeof highlighted.scrollIntoView === 'function') {
      highlighted.scrollIntoView({ block: 'nearest' });
    }
  }

  /**
   * Get initials from a name
   */
  function getInitials(name: string): string {
    const parts = name.split(/\s+/).filter(Boolean);
    if (parts.length === 0) return '?';
    const firstPart = parts[0];
    if (parts.length === 1 || !firstPart) return firstPart?.charAt(0).toUpperCase() ?? '?';
    const lastPart = parts[parts.length - 1];
    return (firstPart.charAt(0) + (lastPart?.charAt(0) ?? '')).toUpperCase();
  }

  // Attach event listeners
  searchInput.addEventListener('input', handleInput);
  searchInput.addEventListener('keydown', handleKeyDown);
  searchInput.addEventListener('focus', () => {
    if (searchQuery.length >= config.minSearchLength || searchResults.length > 0) {
      openDropdown();
    } else if (!isOpen) {
      openDropdown();
      renderResults();
    }
  });

  // Close dropdown when clicking outside
  document.addEventListener('click', (event) => {
    if (!container.contains(event.target as Node)) {
      closeDropdown();
    }
  });

  // Initial render
  renderSelectedUser();
  renderResults();

  return container;
}

/**
 * User Search Picker class for object-oriented usage
 */
export class UserSearchPicker {
  private element: HTMLDivElement;
  private options: UserSearchPickerOptions;
  private _selectedUser: DirectoryUser | null = null;

  constructor(options: UserSearchPickerOptions = {}) {
    this.options = { ...options };
    this._selectedUser = options.selectedUser ?? null;

    // Wrap onSelect to update internal state
    const originalOnSelect = options.onSelect;
    this.options.onSelect = (user) => {
      this._selectedUser = user;
      originalOnSelect?.(user);
    };

    this.element = createUserSearchPicker(this.options);
  }

  /**
   * Get the component element
   */
  getElement(): HTMLDivElement {
    return this.element;
  }

  /**
   * Get the currently selected user
   */
  getSelectedUser(): DirectoryUser | null {
    return this._selectedUser;
  }

  /**
   * Set the selected user programmatically
   */
  setSelectedUser(user: DirectoryUser | null): this {
    this._selectedUser = user;

    // Re-create the component with the new selection
    const newElement = createUserSearchPicker({
      ...this.options,
      selectedUser: user,
    });

    // Replace in DOM if attached
    if (this.element.parentNode) {
      this.element.parentNode.replaceChild(newElement, this.element);
    }

    this.element = newElement;
    return this;
  }

  /**
   * Clear the selection
   */
  clear(): this {
    return this.setSelectedUser(null);
  }

  /**
   * Set the disabled state
   */
  setDisabled(disabled: boolean): this {
    this.options.disabled = disabled;

    // Update input disabled state
    const input = this.element.querySelector('input');
    if (input) {
      input.disabled = disabled;
    }

    // Update clear button disabled state
    const clearBtn = this.element.querySelector('button[aria-label="Clear selected user"]');
    if (clearBtn) {
      (clearBtn as HTMLButtonElement).disabled = disabled;
    }

    return this;
  }

  /**
   * Check if disabled
   */
  isDisabled(): boolean {
    return this.options.disabled ?? false;
  }

  /**
   * Set validation state
   */
  setValidationState(state: ValidationState): this {
    this.options.validationState = state;

    // Update input validation state
    const input = this.element.querySelector('input');
    if (input) {
      // Remove old state classes
      Object.values(STATE_CLASSES).forEach((cls) => {
        input.classList.remove(...cls.split(' '));
      });
      // Add new state classes
      input.classList.add(...STATE_CLASSES[state].split(' '));
    }

    // Update selected display border
    const selectedDisplay = this.element.querySelector('div:nth-child(2)');
    if (selectedDisplay) {
      selectedDisplay.className = selectedDisplay.className.replace(/border-\S+/g, '');
      selectedDisplay.classList.add(STATE_CLASSES[state]);
    }

    return this;
  }

  /**
   * Focus the input
   */
  focus(): this {
    const input = this.element.querySelector('input');
    if (input) {
      input.focus();
    }
    return this;
  }
}
